import { useState, useRef } from 'react';

import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { BackrsUser } from '@backrs_ui/common-ui/src/models/user';
import { SidebarMenuItem } from '@backrs_ui/common-ui/src/molecules/BackrsSidebar/sidebar.types';

import {
  Box,
  SxProps,
  Typography,
  Theme,
  MenuItem,
  Popper,
  Paper,
  Divider,
  ClickAwayListener,
  Grow,
} from '@mui/material';

import { ProfileWidget } from './ProfileWidget';

const FOOTER_ITEM_WIDTH = '264px';

type ProfilePopupWidgetProps = Readonly<{
  backrsUser: BackrsUser;
  topSectionItems: SidebarMenuItem[];
  bottomSectionItems: SidebarMenuItem[];
  isSidebarOpen: boolean;
  isEmulating: boolean;
  handleCloseSidebar: () => void;
}>;

export const ProfilePopupWidget = ({
  backrsUser,
  topSectionItems,
  bottomSectionItems,
  isSidebarOpen,
  isEmulating,
  handleCloseSidebar,
}: ProfilePopupWidgetProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    setIsExpanded(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <Box ref={anchorRef} sx={{ cursor: 'pointer' }}>
          <ProfileWidget
            backrsUser={backrsUser}
            isSidebarOpen={isSidebarOpen}
            isEmulating={isEmulating}
            toggleProfileMenu={() => setIsExpanded((prev) => !prev)}
          />
        </Box>
        <Popper
          open={isExpanded}
          anchorEl={anchorRef.current}
          placement="top-start"
          sx={{ zIndex: 1300 }}
          transition
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout={200} style={{ transformOrigin: 'bottom' }}>
              <Box>
                <ExpandedProfileWidget
                  topSectionItems={topSectionItems}
                  bottomSectionItems={bottomSectionItems}
                  onClose={() => {
                    handleClose();
                    handleCloseSidebar();
                  }}
                />
              </Box>
            </Grow>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

const PopupMenuItem = ({
  id,
  leftIcon,
  title,
  variant,
  hidden,
  onClose,
}: SidebarMenuItem & { onClose: () => void }) => {
  const onClick =
    variant.type === 'NAVIGATION_ITEM'
      ? () => {
          variant.onClick();
          onClose();
        }
      : () => null;

  if (hidden) {
    return undefined;
  }

  return (
    <MenuItem id={id} className="card-menu-item" sx={cardMenuItem} onClick={onClick}>
      <Box className="menu-item-content" sx={menuItemContent}>
        <Box className="icon-and-text" sx={iconAndText}>
          {leftIcon?.iconName && (
            <AppIcon
              iconName={leftIcon.iconName}
              diameter="20px"
              color="Colors/Foreground/fg-quaternary"
            />
          )}
          <Typography sx={menuItemText} variant="text-sm-semibold">
            {title}
          </Typography>
        </Box>
      </Box>
    </MenuItem>
  );
};

type ExpandedProfileWidgetProps = Readonly<{
  topSectionItems: SidebarMenuItem[];
  bottomSectionItems: SidebarMenuItem[];
  onClose: () => void;
}>;

const ExpandedProfileWidget = ({
  topSectionItems,
  bottomSectionItems,
  onClose,
}: ExpandedProfileWidgetProps) => {
  const hasMoreThanOneTopSection = topSectionItems.filter((item) => !item.hidden).length > 1;

  return (
    <Paper elevation={2} sx={expandedMenuPaper}>
      <Box className="menu" sx={menuStyles}>
        <Box className="menu-items-wrapper">
          <Box className="menu-items" sx={menuItems}>
            {topSectionItems.map((item) => (
              <PopupMenuItem
                key={item.title}
                id={item.id}
                leftIcon={item.leftIcon}
                title={item.title}
                variant={item.variant}
                hidden={item.hidden}
                onClose={onClose}
              />
            ))}
          </Box>
          {hasMoreThanOneTopSection && <Divider sx={dividerStyles} />}
          {/* switch account section */}
          <Box className="menu-items"></Box>
        </Box>
        <Box sx={footerItems} className="footer-items">
          {bottomSectionItems.map((item) => (
            <PopupMenuItem
              key={item.title}
              id={item.id}
              leftIcon={item.leftIcon}
              title={item.title}
              variant={item.variant}
              hidden={item.hidden}
              onClose={onClose}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

const footerItems: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: FOOTER_ITEM_WIDTH,
  padding: `${backrs.spacing['spacing-xs']} 0px ${backrs.spacing['spacing-sm']} 0px`,
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xxs'],
});

const dividerStyles: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-secondary'],
  height: '1px',
});

const menuItemText: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  lineHeight: '20px',
});

const iconAndText: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  flex: '1 0 0',
});

const menuItemContent: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  padding: backrs.spacing['spacing-md'],
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  flex: '1 0 0',
});

const cardMenuItem: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  padding: `0px ${backrs.spacing['spacing-sm']}`,
  alignItems: 'center',
  alignSelf: 'stretch',
  border: `1px solid ${backrs.colors['Colors/Base/Transparent']}`,
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
    borderRadius: backrs.radius['radius-xs'],
  },
});

const menuItems: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  padding: `${backrs.spacing['spacing-sm']} 0px`,
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '2px',
  alignSelf: 'stretch',
  width: FOOTER_ITEM_WIDTH,
});

const expandedMenuPaper: SxProps<Theme> = ({ backrs }) => ({
  // not in color system yet - needs a token
  bgcolor: '#F4F4EF',
  borderRadius: `${backrs.radius['radius-xl']} ${backrs.radius['radius-xl']} 16px 16px`,
});

const menuStyles: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: FOOTER_ITEM_WIDTH,
  flexDirection: 'column',
  alignItems: 'flex-start',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  background: backrs.colors['Colors/Base/Transparent'],
  /* Shadows/shadow-lg */
  boxShadow:
    '0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 2px 2px -1px rgba(10, 13, 18, 0.04)',
});
